<template>
  <div class="weather-strip" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
    <!-- Loading -->
    <div v-if="weatherLoading" class="weather-state">
      <b-spinner small variant="primary" label="Loading"></b-spinner>
      <span class="ml-2">{{ $t('weather.loading') }}</span>
    </div>

    <!-- Error / empty -->
    <div v-else-if="weatherError" class="weather-state text-muted">
      {{ errorMessage }}
    </div>

    <!-- Forecast -->
    <div v-else-if="days && days.length" class="weather-scroll">
      <div
        v-for="(day, index) in days"
        :key="day.date || index"
        class="weather-day"
      >
        <div class="weather-day-label">
          {{ dayLabel(day, index) }}
        </div>
        <div class="weather-icon" :title="conditionLabel(day.weatherCode)">
          {{ icon(day.weatherCode) }}
        </div>
        <div class="weather-temp">
          <span class="temp-max">{{ formatNumber(day.tempMax) }}°</span>
          <span class="temp-min">{{ formatNumber(day.tempMin) }}°</span>
        </div>
        <div v-if="day.precipProbability !== null" class="weather-precip">
          💧 {{ formatNumber(day.precipProbability) }}%
        </div>
        <div v-if="day.windMax !== null" class="weather-wind">
          💨 {{ formatNumber(day.windMax) }} {{ $t('weather.kmh') }}
        </div>
      </div>
    </div>

    <!-- No coordinates yet / nothing to show -->
    <div v-else class="weather-state text-muted">
      {{ $t('weather.no_data') }}
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { describeWeatherCode } from '../../services/WeatherService';

export default {
  name: 'WeatherStrip',

  computed: {
    ...mapState({
      days: 'weather',
      weatherLoading: 'weatherLoading',
      weatherError: 'weatherError'
    }),

    errorMessage() {
      // weatherError holds a stable key suffix from WeatherService.
      const map = {
        weather_timeout: 'weather.timeout',
        weather_no_data: 'weather.no_data',
        weather_invalid_coordinates: 'weather.no_data'
      };
      const key = map[this.weatherError] || 'weather.unavailable';
      return this.$t(key);
    }
  },

  methods: {
    dayLabel(day, index) {
      if (index === 0) return this.$t('weather.today');
      const d = new Date(day.date);
      // Guard against a malformed date in the API response so one bad entry
      // can't throw inside $d and break the whole strip render.
      return isNaN(d.getTime()) ? '—' : this.$d(d, 'short');
    },

    icon(code) {
      return describeWeatherCode(code).icon;
    },

    conditionLabel(code) {
      const { key } = describeWeatherCode(code);
      return this.$t(`weather.conditions.${key}`);
    },

    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) return '—';
      // Locale-aware number (matches the rest of the app's use of $n).
      return this.$n(Math.round(value));
    }
  }
};
</script>

<style lang="scss" scoped>
.weather-strip {
  width: 100%;
  min-height: 90px;
}

.weather-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  font-size: 0.9rem;
}

.weather-scroll {
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  padding: 0.25rem 0.5rem 0.5rem;
  -webkit-overflow-scrolling: touch;
}

.weather-day {
  flex: 0 0 auto;
  min-width: 72px;
  text-align: center;
  padding: 0.4rem 0.3rem;
  border-radius: 0.5rem;
  background: #f4f7f4;
  // Flexbox `gap` is unsupported on iOS < 14.5 and stock Android 5.1 WebView,
  // so use margins for spacing instead. Margin direction follows text dir.
  margin-right: 0.5rem;
}
.weather-day:last-child {
  margin-right: 0;
}

[dir='rtl'] .weather-day {
  margin-right: 0;
  margin-left: 0.5rem;
}
[dir='rtl'] .weather-day:last-child {
  margin-left: 0;
}

.weather-day-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #526f2f;
  white-space: nowrap;
}

// Emoji font stack ensures weather/precip/wind glyphs render as color emoji
// (not tofu boxes) across iOS 11+ and older Android WebViews where the
// default font lacks them.
$emoji-font: 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif;

.weather-icon {
  font-size: 1.5rem;
  line-height: 1.6rem;
  font-family: $emoji-font;
}

.weather-temp {
  font-size: 0.8rem;
  .temp-max {
    font-weight: 700;
  }
  .temp-min {
    color: #888;
    margin-left: 0.2rem;
  }
}

.weather-precip,
.weather-wind {
  font-size: 0.68rem;
  color: #555;
  white-space: nowrap;
  font-family: $emoji-font;
}
</style>
